/**
 * Stone Type chips — two-line option chips over Thinkspace's Stone Type select.
 *
 * Copy:
 *   CZ · Clean & Clear
 *   Moissanite · Exceptional Sparkle
 *   Lab-Grown Diamond · Classic Fire, Modern Value
 *   Natural Diamond · Rare & Timeless
 *
 * Sparkle (`sj-option-chip--sparkle`) always lands on the highest tier
 * currently offered for the selected metal:
 *   Natural → Lab-Grown → Moissanite → CZ
 *
 * Depends on: sj-3d-builder.js, sj-metal-chips.css
 */
(function () {
  'use strict';

  var B = window.Sj3dBuilder;
  if (!B) return;

  var STONE_OPTION = 'Stone Type';
  var METAL_OPTION = 'Metal Type';
  var READY = 'data-sj-stone-chips-ready';

  var STONE_COPY = {
    CZ: { title: 'CZ', subtitle: 'Clean & Clear' },
    Moissanite: { title: 'Moissanite', subtitle: 'Exceptional Sparkle' },
    'Diamond - Lab Grown': {
      title: 'Lab-Grown Diamond',
      subtitle: 'Classic Fire, Modern Value',
    },
    'Diamond - Earth-Mined': {
      title: 'Natural Diamond',
      subtitle: 'Rare & Timeless',
    },
  };

  function looksLikeCz(value, text) {
    var hay = (String(value || '') + ' ' + String(text || '')).toLowerCase();
    return /\bcz\b|cubic\s*zirconia/.test(hay);
  }

  /** Last lane we could prove, for steps that render neither select. */
  var lastPsLane = false;

  /**
   * Platinum Silver rings ship with moissanite, so the CZ lane reads as
   * Moissanite. Label only — the option value stays "CZ" because the widget
   * rejoins metal + stone into the "Platinum Silver / CZ" pricing key.
   *
   * The Metal Type select can't be the signal: the builder is a step wizard, so
   * Metal (step 3) is gone from the DOM by the time Stone (step 4) renders. But
   * CZ is only ever paired with Platinum Silver in metal_stone_combinations, so
   * a *selectable* CZ option is itself proof of the Platinum Silver lane.
   */
  function isPlatinumSilverLane(select) {
    var field = document.querySelector('[data-option="' + METAL_OPTION + '"]');
    var metal = field && field.querySelector('select');
    if (metal) {
      lastPsLane = /platinum\s*silver/i.test(String(metal.value || ''));
      return lastPsLane;
    }

    var opts = (select && select.options) || [];
    if (opts.length) {
      for (var i = 0; i < opts.length; i++) {
        if (opts[i].disabled) continue;
        if (String(opts[i].value || '').trim() === '') continue;
        if (looksLikeCz(opts[i].value, opts[i].textContent)) {
          lastPsLane = true;
          return true;
        }
      }
      lastPsLane = false;
      return false;
    }

    return lastPsLane;
  }

  function currentPsLane() {
    var field = document.querySelector('[data-option="' + STONE_OPTION + '"]');
    return isPlatinumSilverLane(field ? field.querySelector('select') : null);
  }

  /** Chip copy depends on the lane, so it must be part of the refresh signature. */
  function labelMode(psLane) {
    return psLane ? 'ps' : 'std';
  }

  function stoneCopy(value, text, psLane) {
    var raw = String(value || '').trim();
    var label = String(text || '').trim();
    if (psLane && looksLikeCz(raw, label)) {
      return STONE_COPY.Moissanite;
    }
    if (STONE_COPY[raw]) return STONE_COPY[raw];

    var hay = (raw + ' ' + label).toLowerCase();
    if (/\bcz\b|cubic\s*zirconia/.test(hay)) return STONE_COPY.CZ;
    if (/moissanite/.test(hay)) return STONE_COPY.Moissanite;
    if (/lab[\s-]*grown|lab\s*diamond/.test(hay)) {
      return STONE_COPY['Diamond - Lab Grown'];
    }
    if (/earth[\s-]*mined|natural\s*diamond/.test(hay)) {
      return STONE_COPY['Diamond - Earth-Mined'];
    }
    if (/diamond/.test(hay)) return STONE_COPY['Diamond - Lab Grown'];

    return {
      title: raw || label || 'Stone',
      subtitle: label && label !== raw ? label : '',
    };
  }

  /** Higher = more premium. */
  function stoneTier(copy) {
    if (!copy) return 0;
    if (copy.title === 'Natural Diamond') return 4;
    if (copy.title === 'Lab-Grown Diamond') return 3;
    if (copy.title === 'Moissanite') return 2;
    if (copy.title === 'CZ') return 1;
    return 0;
  }

  function highestAvailableTitle(opts, psLane) {
    var bestTitle = '';
    var bestTier = 0;
    for (var i = 0; i < opts.length; i++) {
      var copy = stoneCopy(opts[i].value, opts[i].textContent, psLane);
      var tier = stoneTier(copy);
      if (tier > bestTier) {
        bestTier = tier;
        bestTitle = copy.title;
      }
    }
    return bestTitle;
  }

  function ensureMount(field) {
    var mount = field.querySelector('[data-sj-stone-chips]');
    if (!mount) {
      mount = document.createElement('div');
      mount.setAttribute('data-sj-stone-chips', '');
      field.appendChild(mount);
    }
    return mount;
  }

  function optionListSignature(items) {
    var parts = [];
    for (var i = 0; i < items.length; i++) {
      parts.push(
        String(items[i].value || '') + ':' + (items[i].disabled ? '1' : '0')
      );
    }
    return parts.join('|');
  }

  function buildChipButton(value, copy, selected, sparkle) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sj-option-chip';
    if (sparkle) btn.classList.add('sj-option-chip--sparkle');
    btn.classList.toggle('is-selected', value === selected);
    btn.setAttribute('aria-pressed', value === selected ? 'true' : 'false');
    btn.setAttribute(
      'aria-label',
      copy.subtitle ? copy.title + ', ' + copy.subtitle : copy.title
    );

    var text = document.createElement('span');
    text.className = 'sj-option-chip__text';

    var title = document.createElement('span');
    title.className = 'sj-option-chip__title';
    title.textContent = copy.title;
    text.appendChild(title);

    if (copy.subtitle) {
      var sub = document.createElement('span');
      sub.className = 'sj-option-chip__subtitle';
      sub.textContent = copy.subtitle;
      text.appendChild(sub);
    }

    btn.appendChild(text);
    return btn;
  }

  /**
   * The native select is only clipped, not removed — screen readers still read it
   * and it shows if the CSS fails. Retitle the option, never its value.
   */
  function relabelNativeOptions(select, psLane) {
    var opts = select.options || [];
    for (var i = 0; i < opts.length; i++) {
      var opt = opts[i];
      if (!looksLikeCz(opt.value, opt.textContent)) continue;
      var want = psLane
        ? STONE_COPY.Moissanite.title
        : String(opt.value || '').trim();
      if (want && opt.textContent.trim() !== want) opt.textContent = want;
    }
  }

  function displayOpts(opts, psLane) {
    return opts.slice().sort(function (a, b) {
      var ra = stoneTier(stoneCopy(a.value, a.textContent, psLane));
      var rb = stoneTier(stoneCopy(b.value, b.textContent, psLane));
      return ra - rb;
    });
  }

  function renderStoneChips(field) {
    var select = field.querySelector('select');
    if (!select) return;
    var psLane = isPlatinumSilverLane(select);
    relabelNativeOptions(select, psLane);

    var mount = ensureMount(field);
    var current = String(select.value || '');
    var opts = Array.from(select.options || []).filter(function (opt) {
      return !opt.disabled && String(opt.value || '').trim() !== '';
    });
    if (!opts.length) return;

    var sig = optionListSignature(opts);
    // Thinkspace lists stones in catalog order, which puts lab ahead of the
    // platinum moissanite chip. Keep the visible stack Moissanite, Lab, Natural.
    opts = displayOpts(opts, psLane);

    var wrap = document.createElement('div');
    wrap.className = 'sj-option-chips sj-stone-chips';
    wrap.setAttribute('data-sj-sig', sig);
    wrap.setAttribute('data-sj-mode', labelMode(psLane));
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', 'Stone Type');

    var sparkleTitle = highestAvailableTitle(opts, psLane);

    opts.forEach(function (opt) {
      var value = String(opt.value).trim();
      var copy = stoneCopy(value, opt.textContent, psLane);
      var btn = buildChipButton(
        value,
        copy,
        current,
        Boolean(sparkleTitle && copy.title === sparkleTitle)
      );
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        B.setSelectValue(select, value);
        window.setTimeout(function () {
          refreshStoneChips(field);
        }, 0);
      });
      wrap.appendChild(btn);
    });

    mount.innerHTML = '';
    mount.appendChild(wrap);
    field.classList.add('sj-stone-chips-host');
  }

  function refreshStoneChips(field) {
    var select = field.querySelector('select');
    if (!select) return;
    var psLane = isPlatinumSilverLane(select);
    relabelNativeOptions(select, psLane);
    var opts = Array.from(select.options || []).filter(function (opt) {
      return !opt.disabled && String(opt.value || '').trim() !== '';
    });
    var mount = field.querySelector('[data-sj-stone-chips]');
    var wrap = mount && mount.querySelector('.sj-stone-chips');
    var buttons = wrap && wrap.querySelectorAll('.sj-option-chip');
    var sig = optionListSignature(opts);
    if (
      wrap &&
      buttons &&
      wrap.getAttribute('data-sj-sig') === sig &&
      wrap.getAttribute('data-sj-mode') === labelMode(psLane) &&
      buttons.length === opts.length
    ) {
      var current = String(select.value || '');
      var shown = displayOpts(opts, psLane);
      for (var i = 0; i < buttons.length; i++) {
        var on = String(shown[i].value).trim() === current;
        buttons[i].classList.toggle('is-selected', on);
        buttons[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      return;
    }
    renderStoneChips(field);
  }

  function enhanceStoneField(field) {
    if (!field || field.getAttribute(READY) === '1') return;
    var select = field.querySelector('select');
    if (!select) return;

    field.setAttribute(READY, '1');
    field._sjStoneSelect = select;
    renderStoneChips(field);

    var refresh = B.rafDebounce
      ? B.rafDebounce(function () {
          refreshStoneChips(field);
        })
      : function () {
          refreshStoneChips(field);
        };
    var mo = new MutationObserver(refresh);
    mo.observe(select, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value', 'selected', 'disabled'],
    });
    select.addEventListener('change', refresh);
    field._sjStoneMo = mo;
  }

  function rewriteCz(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    while (walker.nextNode()) {
      var node = walker.currentNode;
      var text = node.textContent || '';
      if (!/\bCZ\b/.test(text)) continue;
      node.textContent = text.replace(/\bCZ\b/g, STONE_COPY.Moissanite.title);
    }
  }

  /**
   * The widget's own "your selections" recap prints the raw stone value and sits
   * outside [data-option], so the chips and our CSS never reach it. Also catches
   * summary lines that print the joined "Platinum Silver / CZ" string.
   */
  function relabelRecap(widget) {
    if (!currentPsLane()) return;
    var moi = STONE_COPY.Moissanite.title;
    var nodes = widget.querySelectorAll('*');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.children.length) continue;
      var own = (el.textContent || '').trim();
      if (own === 'Stone Type:') {
        var value = el.nextElementSibling;
        if (value) rewriteCz(value);
      } else if (own.indexOf('Platinum Silver / CZ') !== -1) {
        el.textContent = own.split('Platinum Silver / CZ').join(
          'Platinum Silver / ' + moi
        );
      }
    }
  }

  function scan(widget) {
    document
      .querySelectorAll('[data-option="' + STONE_OPTION + '"]')
      .forEach(function (field) {
        // Thinkspace sometimes swaps the whole select on re-render, which orphans
        // our observer — re-arm on a new node instead of going stale.
        var select = field.querySelector('select');
        if (select && field._sjStoneSelect && field._sjStoneSelect !== select) {
          if (field._sjStoneMo) field._sjStoneMo.disconnect();
          field.removeAttribute(READY);
        }
        if (field.getAttribute(READY) === '1') {
          refreshStoneChips(field);
          return;
        }
        enhanceStoneField(field);
      });
    if (widget) relabelRecap(widget);
  }

  B.bootOnWidget(function (widget) {
    B.watchWidget(widget, function () {
      scan(widget);
    });
  });
})();
